import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [company, setCompany] = useState(null);
    const [modules, setModules] = useState(null);
    const [loading, setLoading] = useState(true);
    const tokenRef = useRef(localStorage.getItem('token'));
    const [token, setToken] = useState(tokenRef.current);
    const fetchedRef = useRef(false);

    const fetchUser = useCallback(async (authToken) => {
        if (!authToken) {
            setLoading(false);
            return;
        }
        try {
            const response = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (response.data.user) {
                setUser(response.data.user);
                setCompany(response.data.company);
                setModules(response.data.modules || null);
            } else {
                setUser(response.data);
            }
        } catch (error) {
            localStorage.removeItem('token');
            tokenRef.current = null;
            setToken(null);
            setUser(null);
            setCompany(null);
            setModules(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!fetchedRef.current) {
            fetchedRef.current = true;
            // If we're returning from Google OAuth, let AuthCallback handle session
            // before we try /auth/me (which would 401 with the stale token).
            if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
                setLoading(false);
                return;
            }
            fetchUser(tokenRef.current);
        }
    }, [fetchUser]);

    const login = async (userData, companyData, authToken) => {
        localStorage.setItem('token', authToken);
        tokenRef.current = authToken;
        setToken(authToken);
        setUser(userData);
        setCompany(companyData);
    };

    const logout = () => {
        localStorage.removeItem('token');
        tokenRef.current = null;
        setToken(null);
        setUser(null);
        setCompany(null);
    };

    const ownerSignUp = async (data) => {
        const response = await axios.post(`${API}/auth/owner/signup`, data);
        await login(response.data.user, response.data.company, response.data.token);
        return response.data;
    };

    const ownerSignIn = async (email, password) => {
        const response = await axios.post(`${API}/auth/owner/signin`, { email, password });
        await login(response.data.user, response.data.company, response.data.token);
        return response.data;
    };

    const employeeLogin = async (companySlug, username, password) => {
        const response = await axios.post(`${API}/auth/employee/login`, {
            company_slug: companySlug,
            username,
            password
        });
        await login(response.data.user, response.data.company, response.data.token);
        return response.data;
    };

    const superAdminLogin = async (password) => {
        const response = await axios.post(`${API}/auth/superadmin/login`, { password });
        await login(response.data.user, null, response.data.token);
        return response.data;
    };

    const value = {
        user,
        company,
        modules,
        setModules,
        token,
        loading,
        login,
        logout,
        ownerSignUp,
        ownerSignIn,
        employeeLogin,
        superAdminLogin,
        isAuthenticated: !!user,
        isOwner: user?.type === 'owner',
        isEmployee: user?.type === 'employee',
        // Platform-only super admin (legacy password-mode, no company)
        isPlatformOnlyAdmin: user?.type === 'superadmin',
        // Super admin via role (Google login or DB-assigned), OR legacy
        isSuperAdmin: user?.type === 'superadmin' || user?.role === 'super_admin',
        isAdmin: user?.type === 'owner' || user?.is_admin
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
